import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { CreditCard, Hand, ScanLine, Sparkles, Loader2, X, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import BarcodeScanner from "@/components/BarcodeScanner";

// -----------------------------------------------------------------------------
// PriorityCard — a tier-based digital membership card. The card also carries
// the "Hand-2-Pay" flow: the holder taps `Show pay QR`, we mint a short-lived
// signed token from /priority-card/hand2pay/mint, and render it overlaid on
// a stylised palm. The receiver scans it with the built-in scanner, which
// posts to /priority-card/hand2pay/verify to complete the transfer.
//
// Full biometric palm-print enrolment is on the roadmap (see PRD).
// -----------------------------------------------------------------------------
const TIER_META = {
  standard: { grad: "from-stone-600 to-stone-800",   perks: ["Basic listing boost"] },
  silver:   { grad: "from-slate-400 to-slate-600",   perks: ["3× listing boost", "Priority helpdesk"] },
  gold:     { grad: "from-amber-500 to-yellow-700",  perks: ["5× listing boost", "Featured badge", "Priority helpdesk"] },
  platinum: { grad: "from-fuchsia-600 to-indigo-700", perks: ["10× listing boost", "Featured badge", "Concierge helpdesk", "Free live GPS map"] },
};

export default function PriorityCard() {
  const { user } = useAuth();
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payTicket, setPayTicket] = useState(null);      // active outgoing QR
  const [amountRupees, setAmountRupees] = useState("");
  const [note, setNote] = useState("");
  const [scanning, setScanning] = useState(false);
  const [history, setHistory] = useState([]);
  const timerRef = useRef(null);

  useSeo({ title: "Priority Membership Card · find2near",
           description: "Tier-based membership card with Hand-2-Pay quick-transfer QR (palm-overlay).",
           canonical: "/priority-card" });

  const load = () => {
    api.get("/priority-card/me").then((r) => setCard(r.data)).catch(() => {});
    api.get("/priority-card/hand2pay/history").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  const upgrade = async (tier) => {
    setBusy(true);
    try {
      const r = await api.post("/priority-card/enroll", { tier });
      toast.success(`Upgraded to ${r.data.tier}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // ---------- Hand-2-Pay: mint & auto-refresh --------------------------------
  const mint = async () => {
    setBusy(true);
    try {
      const amount = amountRupees ? Math.round(parseFloat(amountRupees) * 100) : 0;
      const r = await api.post("/priority-card/hand2pay/mint", { amount_paise: amount, note, ttl_seconds: 90 });
      setPayTicket(r.data);
      // schedule auto-expiry so the UI clears once the token dies
      clearTimeout(timerRef.current);
      const msLeft = new Date(r.data.expires_at).getTime() - Date.now();
      timerRef.current = setTimeout(() => setPayTicket(null), Math.max(1000, msLeft));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const handleScanned = async (data) => {
    // If a hand2pay QR is scanned, complete the transfer.
    let payload = null;
    try { payload = JSON.parse(data.code); } catch { /* not JSON */ }
    if (!payload || payload.kind !== "f2n_hand2pay") {
      toast.message("That QR isn't a Hand-2-Pay code.");
      return;
    }
    try {
      const r = await api.post("/priority-card/hand2pay/verify", { token: payload.token });
      toast.success(`Received ₹${(r.data.amount_paise / 100).toLocaleString("en-IN")} from ${r.data.sender_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
  };

  if (!user) {
    return <div className="max-w-md mx-auto text-center py-24 px-6">
      <CreditCard className="mx-auto text-fuchsia-600" size={44} />
      <h2 className="heading text-2xl font-black mt-4">Priority membership</h2>
      <p className="text-stone-500 mt-2 text-sm">Sign in to claim your card and start Hand-2-Pay transfers.</p>
    </div>;
  }
  if (!card) return <div className="p-16 text-center text-stone-400"><Loader2 className="mx-auto animate-spin" /></div>;

  const meta = TIER_META[card.tier] || TIER_META.standard;
  const qrValue = payTicket ? JSON.stringify({ kind: "f2n_hand2pay", token: payTicket.token, sender_id: payTicket.sender_id, expires_at: payTicket.expires_at }) : "";

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-5" data-testid="priority-card">
      <BarcodeScanner open={scanning} onClose={() => setScanning(false)} onScanned={handleScanned} mode="qr" />

      {/* Header */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><CreditCard size={12} className="text-fuchsia-600" /> Priority membership card</div>
        <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Your card. <span className="text-fuchsia-600">Your palm.</span></h1>
        <p className="text-stone-500 text-sm mt-1">Tap a hand, transfer instantly — no phone number, no UPI ID, no fuss.</p>
      </div>

      <div className="grid gap-5 md:grid-cols-[380px_1fr]">
        {/* --------- Card visual --------- */}
        <div className={`rounded-3xl p-5 text-white bg-gradient-to-br ${meta.grad} shadow-xl`} data-testid="priority-card-visual">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-black opacity-70">find2near · priority</div>
            <div className="text-[10px] uppercase tracking-widest font-black">{card.tier}</div>
          </div>
          <div className="mt-6 font-mono text-xl tracking-widest opacity-90">•••• •••• •••• {(card.user_id || "").slice(-4).toUpperCase()}</div>
          <div className="mt-4 flex items-end justify-between">
            <div>
              <div className="text-[9px] uppercase tracking-widest opacity-60">Cardholder</div>
              <div className="font-black">{card.name}</div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-widest opacity-60">Since</div>
              <div className="font-mono text-xs">{(card.issued_at || "").slice(0, 10)}</div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-white/20 text-[11px] flex flex-wrap gap-1">
            {meta.perks.map((p) => <span key={p} className="bg-white/15 rounded-full px-2 py-0.5">✦ {p}</span>)}
          </div>
        </div>

        {/* --------- Hand-2-Pay panel --------- */}
        <div className="rounded-3xl bg-white border border-stone-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Hand className="text-fuchsia-700" size={16} />
            <div className="font-black text-stone-900">Hand-2-Pay</div>
            <span className="text-[10px] font-black uppercase tracking-widest bg-fuchsia-100 text-fuchsia-800 rounded-full px-2 py-0.5">Beta</span>
          </div>
          <div className="text-xs text-stone-600 leading-relaxed mb-3">
            Show this QR on your palm — the receiver scans it and the transfer runs.
            Codes expire in 90 seconds and are single-use for safety.
          </div>

          {payTicket ? (
            <div className="relative w-full max-w-xs mx-auto">
              {/* Stylised palm silhouette behind the QR */}
              <div className="aspect-square rounded-full bg-gradient-to-br from-orange-100 via-amber-100 to-rose-100 border-2 border-amber-200 grid place-items-center relative overflow-hidden">
                <Hand size={220} className="text-amber-300/70 absolute" strokeWidth={0.5} />
                <div className="relative z-10 bg-white p-3 rounded-2xl border border-stone-200 shadow">
                  <QRCodeCanvas value={qrValue} size={180} level="M" includeMargin data-testid="hand2pay-qr" />
                </div>
              </div>
              <div className="mt-3 text-center">
                {payTicket.amount_paise ? (
                  <div className="text-lg font-black text-stone-900 inline-flex items-center"><IndianRupee size={14} />{(payTicket.amount_paise/100).toLocaleString("en-IN")}</div>
                ) : <div className="text-sm text-stone-600">Receiver enters amount</div>}
                <div className="text-[10px] text-stone-500 mt-1">Expires {payTicket.expires_at.slice(11, 19)} UTC · single-use</div>
                <Button onClick={() => setPayTicket(null)} variant="ghost" className="mt-1 text-xs rounded-full">Cancel code</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <Input type="number" step="0.01" value={amountRupees} onChange={(e) => setAmountRupees(e.target.value)} placeholder="Amount ₹ (optional)" className="rounded-2xl h-10 font-mono col-span-2" data-testid="h2p-amount" />
                <Button onClick={mint} disabled={busy} className="rounded-full h-10 bg-fuchsia-600 hover:bg-fuchsia-700 font-black" data-testid="h2p-mint">
                  {busy ? <Loader2 className="animate-spin" size={14} /> : "Show QR"}
                </Button>
              </div>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="rounded-2xl h-10" data-testid="h2p-note" />
              <Button onClick={() => setScanning(true)} variant="outline" className="w-full rounded-full h-10 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="h2p-scan">
                <ScanLine size={14} className="mr-1.5" /> Receive · scan a hand
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* --------- Upgrade tiers --------- */}
      <div className="rounded-3xl bg-white border border-stone-200 p-5">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">Upgrade tier</div>
        <div className="grid gap-2 md:grid-cols-4">
          {Object.entries(TIER_META).map(([tier, m]) => (
            <button key={tier} onClick={() => upgrade(tier)} disabled={busy || tier === card.tier}
                    className={`rounded-2xl text-left p-3 border-2 ${card.tier === tier ? "border-fuchsia-500 bg-fuchsia-50" : "border-stone-200 hover:border-stone-300 bg-white"} disabled:opacity-60`}
                    data-testid={`upgrade-${tier}`}>
              <div className="text-xs uppercase tracking-widest font-black text-stone-500">{tier}</div>
              <div className="text-sm text-stone-700 mt-1">{m.perks.slice(0, 2).join(" · ")}</div>
              {card.tier === tier && <div className="text-[10px] text-fuchsia-700 font-black mt-1">Current</div>}
            </button>
          ))}
        </div>
      </div>

      {/* --------- History --------- */}
      <div className="rounded-3xl bg-white border border-stone-200 p-5">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">Recent transfers</div>
        {history.length === 0 ? (
          <div className="text-sm text-stone-400 py-4 text-center">No transfers yet — try Hand-2-Pay above.</div>
        ) : (
          <ul className="text-sm divide-y divide-stone-100">
            {history.map((h) => {
              const iSent = h.sender_id === user.user_id;
              return (
                <li key={h.tx_id} className="py-2 flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full grid place-items-center text-white font-black ${iSent ? "bg-rose-500" : "bg-emerald-600"}`}>{iSent ? "-" : "+"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold">{iSent ? `→ ${h.receiver_name}` : `← ${h.sender_name}`}</div>
                    <div className="text-xs text-stone-500">{h.note || "Hand-2-Pay"}</div>
                  </div>
                  <div className="font-mono font-black text-right">₹{(h.amount_paise / 100).toLocaleString("en-IN")}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2">
        <Sparkles size={13} className="mt-0.5 shrink-0" />
        <div>
          <b>Coming soon:</b> full palm-print biometric enrolment (Amazon-One style, IR-hardware + trained ML). The current QR-on-palm flow gives you the same speed today, without the biometric risk.
        </div>
      </div>
    </div>
  );
}
